import { MemberRole } from '@/__generated__/graphql';

/**
 * Client-side mirror of the server RBAC matrix
 * (`apps/api/src/logic/security/rbac.ts` `RbacService`).
 *
 * The server is the ONLY authority — every mutation is gated by
 * `@RequirePermission(surface, action)` and the `PermissionGuard`. This mirror
 * exists purely to keep the CLIENT honest: a control the server would reject
 * must never render enabled (a dead-end that fails on click with a generic
 * "try again" that never works). It is the systemic form of the one-off owner
 * gate `BillingPage` / `WorkspaceSettingsPage` already do inline.
 *
 * Keyed by the wire `MemberRole` enum (UPPERCASE) so callers gate straight off
 * `useCurrentTenantRole()` without a case conversion. The grant model matches
 * the server exactly: each (role, surface) maps to the HIGHEST action the role
 * may perform there; higher actions imply the lower ones
 * (`admin` ⇒ `edit` ⇒ `view`). An omitted surface means no access at all.
 *
 * When the server matrix changes, this file changes in the same PR — the two
 * must never drift (the same discipline `rbac-view-model.ts` documents).
 */

export const DASHBOARD_SURFACES = [
  'overview',
  'agent',
  'playground',
  'catalog',
  'surfaces',
  // UGL-107 — audience governance (mirrors the server rbac.ts surface; grants
  // mirror `catalog`, so the split never changes a role's effective access).
  'audiences',
  // DE-006 — the documentation corpus. Its own surface so docs access can be
  // granted without granting system-prompt rewrite; `edit` drafts and ingests,
  // `admin` is the publish-to-end-users authority (server `rbac.ts` carries
  // the full reasoning).
  'documentation',
  'analytics',
  'integrations',
  'api-keys',
  'members',
  'billing',
  'audit-log',
  'settings',
] as const;
export type DashboardSurface = (typeof DASHBOARD_SURFACES)[number];

/**
 * The `surface` value for something every role may see, whatever their grants.
 *
 * It exists so that "every role" is something a row SAYS rather than something
 * it fails to say. An omitted key used to mean exactly this on the sidebar
 * definitions, and the two are indistinguishable in review — which is how
 * thirteen nav rows came to be everyone's by accident (D-9 / ADR-0549).
 *
 * Lives here rather than beside the sidebar because it is a statement about the
 * PERMISSION model, and two consumers now need it: the sidebar rows that are
 * open to every member, and the settings-search registry, whose `/account`
 * entries belong to whoever is signed in and to no surface grant at all.
 */
export const EVERY_ROLE_SURFACE = 'every-role';

/** A surface, or the sentinel for "open to every role". */
export type DashboardSurfaceOrEveryRole = DashboardSurface | typeof EVERY_ROLE_SURFACE;

export type DashboardAction = 'view' | 'edit' | 'admin';

const ACTION_RANK: Record<DashboardAction, number> = { view: 1, edit: 2, admin: 3 };

type SurfaceGrants = Partial<Record<DashboardSurface, DashboardAction>>;

/** A grant of the same action on every surface (owner / reviewer). */
function uniformGrant(action: DashboardAction): SurfaceGrants {
  const grants: SurfaceGrants = {};
  for (const surface of DASHBOARD_SURFACES) {
    grants[surface] = action;
  }
  return grants;
}

/**
 * The permission matrix — mirrors `PERMISSION_MATRIX` in the server's
 * `rbac.ts`. Each role's highest action per surface; an omitted surface means
 * "no access".
 */
const PERMISSION_MATRIX: Record<MemberRole, SurfaceGrants> = {
  // Owner: full control everywhere, including billing + ownership transfer.
  [MemberRole.Owner]: uniformGrant('admin'),

  // Admin: administers everything except billing, which stays owner-reserved
  // (billing PLAN changes need billing:admin — the owner; billing SETTINGS need
  // only billing:edit, which the admin holds).
  [MemberRole.Admin]: { ...uniformGrant('admin'), billing: 'edit' },

  // Editor: edits product/content surfaces; reads observability; no members /
  // billing access.
  [MemberRole.Editor]: {
    overview: 'view',
    agent: 'edit',
    playground: 'edit',
    catalog: 'edit',
    surfaces: 'edit',
    audiences: 'edit',
    // DE-006 — `edit`, not `admin`: every documentation power an editor has
    // today, without the new publish-to-end-users authority.
    documentation: 'edit',
    integrations: 'edit',
    analytics: 'view',
    'api-keys': 'view',
    'audit-log': 'view',
    settings: 'view',
  },

  // DE-006 — Documentation: owns the documentation corpus and nothing else.
  // Mirrors the server row exactly, including what is ABSENT: no `agent`,
  // which is the whole reason the role exists (granting the docs used to mean
  // granting the system-prompt rewrite). `analytics: view` because a role that
  // can write the docs and cannot see whether they are used cannot do the job.
  [MemberRole.Documentation]: {
    overview: 'view',
    documentation: 'edit',
    analytics: 'view',
  },

  // Reviewer: read-only oversight across EVERY surface (the compliance persona).
  [MemberRole.Reviewer]: uniformGrant('view'),

  // Viewer: read-only on product surfaces only; sensitive surfaces (members,
  // billing, audit-log, api-keys, settings) are invisible.
  [MemberRole.Viewer]: {
    overview: 'view',
    agent: 'view',
    playground: 'view',
    catalog: 'view',
    surfaces: 'view',
    audiences: 'view',
    // DE-006 — product content, not a sensitive surface.
    documentation: 'view',
    analytics: 'view',
    integrations: 'view',
  },

  // Analyst: the customer-success seat (D-9 / ADR-0549). Two grants and eleven
  // refusals — every other role holding `analytics:view` also holds catalog,
  // playground, surfaces and agent, so a CS invite used to arrive in a developer
  // product. The sidebar is `accessibleSurfacesForRole`, so subtracting the
  // grants IS subtracting the doors. `overview` rides along because it is the
  // dashboard's landing route, and a role that may not read its own entry point
  // signs in to something that looks broken.
  [MemberRole.Analyst]: {
    overview: 'view',
    analytics: 'view',
  },
};

/** True when `role` may perform `action` on `surface`. Default-deny. */
export function roleCan(
  role: MemberRole,
  surface: DashboardSurface,
  action: DashboardAction,
): boolean {
  const grant = PERMISSION_MATRIX[role][surface];
  if (grant === undefined) {
    return false;
  }
  return ACTION_RANK[grant] >= ACTION_RANK[action];
}

/** The surfaces `role` can at least view (drives sidebar visibility). */
export function accessibleSurfacesForRole(role: MemberRole): DashboardSurface[] {
  return DASHBOARD_SURFACES.filter((surface) => roleCan(role, surface, 'view'));
}

/**
 * ADR-0347 — the mirror of the server's `CAPABILITY_APPROVE_ROLES`: who may read
 * and decide pending two-person-rule enablement requests.
 *
 * Deliberately NOT a rank in the `view < edit < admin` chain. The approver a
 * compliance workflow most wants is the read-only `Reviewer`, whom rank excludes
 * by construction, while rank would implicitly grant approve to every `edit`
 * holder. `Editor` is included so a two-editor workspace can actually use dual
 * control; `Viewer` is excluded.
 *
 * The server is the sole authority — this mirror exists only so the UI can
 * disable a control with a reason instead of rendering one that fails on click.
 * `apps/api/src/logic/security/rbac.ts` is the source of truth; the two are kept
 * in lockstep by `dashboard-permissions.test.ts`.
 */
export const CAPABILITY_APPROVE_ROLES: readonly MemberRole[] = [
  MemberRole.Owner,
  MemberRole.Admin,
  MemberRole.Editor,
  MemberRole.Reviewer,
];

/** True when `role` may decide pending enablement requests. Default-deny. */
export function roleCanApproveCapabilityEnablement(role: MemberRole): boolean {
  return CAPABILITY_APPROVE_ROLES.includes(role);
}
